<template>
  <div class="MapBox">
    <MapBox
      class="MapBox__wrapper" 
      :accessToken="accessToken"
      :mapStyle.sync="mapStyle"
      :options="options"
      @load="onMapLoaded">
    </MapBox>
    
    <TileData
      v-if="showTileData"
      class="MapBox__data"
      :loaded="loaded" />

  </div>
</template>

<script>
import MapBox from '@/components/MapBox'
import mapboxgl from 'mapbox-gl'

import TileData from 'molecule/TileData'

import { mapGetters, mapMutations } from 'vuex'

import { accessToken, mapStyle } from 'config/mapbox'

import { ButtonControl } from '@/services/mapbox'
import { generateExport } from '@/services/pdfexport.js'

export default {
  components: {
    MapBox, TileData
  },
  props: {
    showTileData: {
      type: Boolean,
      default: true
    },
    showControls: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loaded: false,
      accessToken,
      mapStyle,
      options: {
        maxBounds: [ // Netherlands
          [3.01497114423, 50.503721015],
          [7.39205325687, 53.8104033474]
        ]
      }
    }
  },
  computed: {
    ...mapGetters('map', [
      'getFilterStatus',
      'getHighlighted',
      'getSymbolLayerId',
      'getActiveLayerInfo'
    ]),
    ...mapGetters('filters', [
      'getFilterInfo',
      'getDisabledFilters'
    ]),
    ...mapGetters('config', {
      planMode: 'planmode',
      layerConfig: 'layers'
    }),
    ...mapGetters('filters', [
      'getActiveNeighborhood',
      'getActiveMunicipalityInfo',
      'getActiveNeighborhoodInfo'
    ]),
    ...mapGetters('geodata', [
      'getNeighborhoodCoordinates',
      'generateTopVisibleBarcodesForGIS',
      'generateRemainingVisibleBarcodesForGIS'
    ]),
    // The paint instructions
    fillHexagons() {
      let fillCase = [
        "case",
        [ 
          "!=", ["get", "BU_CODE"], this.getActiveNeighborhood
        ],
        "hsla(0, 0%, 0%, 0)",
      ]

      if (this.getFilterStatus.displaymode === 'barcode') {

        // The number of colors on the map follows the number of items in the legend.
        let top = this.generateTopVisibleBarcodesForGIS({ count: this.getActiveLayerInfo.items.length - 1 })
        let remaining = this.generateRemainingVisibleBarcodesForGIS({ count: this.getActiveLayerInfo.items.length - 1 })

        // specs = {eigendom: "Eigenaar bewoner", energielabel: "G", wozwaardeklasse: "€200.000 tot €250.000"}
        // A key => value object equal to layer prop => condition
        top.forEach((specs, index) => {
          let condition = ['all']
          Object.keys(specs).forEach(prop => {
            condition.push([
              "==", ["get", prop], specs[prop]
            ])
          })

          if (condition.length !== 1) {
            fillCase.push(condition)
            // Get the color through the legend details
            fillCase.push(this.getActiveLayerInfo.items[index].color)
          }
        })

        let color = this.getActiveLayerInfo.items[(this.getActiveLayerInfo.items.length - 1)].color
        
        let condition = ['all']
        Object.keys(remaining).forEach(prop => {
          condition.push([
            "in", ["get", prop], [ "literal", remaining[prop] ]
          ])
        })

        if (condition.length !== 1) {
          fillCase.push(condition)
          // Get the color through the legend details
          fillCase.push(color)
        }
      
      } else {

        // Hide those objects that meet any of the disabling criteria
        Object.keys(this.getDisabledFilters).forEach(key => {
          this.getDisabledFilters[key].forEach(item => {
            fillCase.push([
              '==', ['get', key], item.value || item.label
            ])
            fillCase.push("hsla(0, 0%, 0%, 0)")
          })
        })
        
        // Paint the active items with the appropriate color
        if (this.getActiveLayerInfo) {
          let prop = this.getActiveLayerInfo.categorical ? `${this.getActiveLayerInfo.id}_code` : this.getActiveLayerInfo.id

          this.getActiveLayerInfo.items.forEach(item => {
            if (item.active) {
              fillCase.push([
                "==", ["get", prop], item.value || item.label
              ])
              fillCase.push(item.color)
            }
          })
        }      
      }

      // Default (not used, but required by MapBox)
      fillCase.push("hsla(0, 0%, 0%, 0)")

      return fillCase      
    },
    fillBorders() {
      return [
        "case",
        [
          "!=", ["get", "BU_CODE"], this.getActiveNeighborhood
        ],
        0,
        0.3
      ]
    },
    fillBlocks() {
      if (this.getFilterStatus.displaymode === 'bouwblok-auto') {
        return "rgba(0,0,0,0)"
      }
      
      return [
        "case",
        [
          "!=", ["get", "BU_CODE"], this.getActiveNeighborhood
        ],
        "rgba(0,0,0,0)",
        "black"
      ] 
    },
    fillAutoBlocks() {
      if (this.getFilterStatus.displaymode !== 'bouwblok-auto') {
        return "rgba(0,0,0,0)"
      }
      
      return [
        "case",
        [
          "!=", ["get", "BU_CODE"], this.getActiveNeighborhood
        ],
        "rgba(0,0,0,0)",
        "black"
      ] 
    },
    buildingOutlineOpacity() {
      return [
        "case",
        [
          '==', ["get", "pand_id"], this.getHighlighted
        ],
        "red",
        [
          "!=", ["get", "BU_CODE"], this.getActiveNeighborhood
        ],
        "rgba(0,0,0,0)",
        "rgba(255,255,255,0.6)"
      ]
    }
  },
  watch: {
    getDisabledFilters: {
      handler: function() {
        if (this.$store.map) {
          this.$store.map.setPaintProperty('hexagons', 'fill-color', this.fillHexagons)
          this.$store.map.setPaintProperty('border', 'line-opacity', this.fillBorders)
          this.$store.map.setPaintProperty('hexagons_outline', 'line-color', this.buildingOutlineOpacity)
        }
      },
      deep: true
    },
    getActiveNeighborhood(){
      this.$store.map.setPaintProperty('hexagons', 'fill-color', this.fillHexagons)
      this.$store.map.setPaintProperty('border', 'line-opacity', this.fillBorders)
      if (this.layerConfig.blokken_manual.active) {
        this.$store.map.setPaintProperty('blokken_manual', 'line-color', this.fillBlocks)
      }
      if (this.layerConfig.blokken_auto.active) {
        this.$store.map.setPaintProperty('blokken_auto', 'line-color', this.fillAutoBlocks)
      }
      this.$store.map.setPaintProperty('hexagons_outline', 'line-color', this.buildingOutlineOpacity)

      this.zoomToNeighborhood()
    },
    getActiveLayerInfo: {
      handler: function() {
        if (this.$store.map) {
          this.$store.map.setPaintProperty('hexagons', 'fill-color', this.fillHexagons)
          this.$store.map.setPaintProperty('border', 'line-opacity', this.fillBorders)
          if (this.layerConfig.blokken_manual.active) {
            this.$store.map.setPaintProperty('blokken_manual', 'line-color', this.fillBlocks)
          }
          if (this.layerConfig.blokken_auto.active) {
            this.$store.map.setPaintProperty('blokken_auto', 'line-color', this.fillAutoBlocks)
          }
          this.$store.map.setPaintProperty('hexagons_outline', 'line-color', this.buildingOutlineOpacity)
        }
      }, 
      deep: true
    },
    getHighlighted: function() {
      try {
        if (this.$store.map) {
          this.$store.map.setPaintProperty('hexagons_outline', 'line-color', this.buildingOutlineOpacity)
        }
      } catch(e) {} // For some reason this still occasionally crashes. With no consquences, so ignore worthy
    }
  },
  methods: {
    ...mapMutations('map', [
      'setLoadedStatus',
      'setLayerAvailability',
      'setSymbolLayerId'
    ]),
    onMapLoaded(event) {
      // Note: a reference to the map has to be stored in a non-reactive manner.
      this.$store.map = event.map

      if (this.showControls) {
        let nav = new mapboxgl.NavigationControl({
          showCompass: false,
          showZoom: true
        });
        this.$store.map.addControl(nav, 'top-right');


        this.$store.map.addControl(
          new ButtonControl({
            label: 'Download Map',
            callback: this.downloadMap
          }), 'bottom-left'
        )
      }

      this.addLayers()
      this.zoomToNeighborhood()

      // Signal to the TileData component that the map is done loading
      this.loaded = true
      this.setLoadedStatus({ status: true })
    },
    addLayers() {
      
      // We use this to add our layers below text labels
      let firstSymbolId = this.getFirstSymbolLayerId()
      this.setSymbolLayerId({ id: firstSymbolId })

      // The area border
      if (this.layerConfig.border.active) {
        this.$store.map.addSource('border', {
          type: 'vector',
          url: this.layerConfig.border.url
        })
        this.$store.map.addLayer({ 
          "id": "border",
          "type": "line",
          "source": "border",
          "source-layer": this.layerConfig.border.source,
          "paint": {
            "line-width": 2,
            "line-color": "black", // "hsla(23,100%, 62%, 0.5)",
            "line-opacity": this.fillBorders
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'border', status: true })
      }
      
      // The main layer: the hexagons
      if (this.layerConfig.hexagons.active) {
        this.$store.map.addSource('hexagons', {
          type: 'vector',
          url: this.layerConfig.hexagons.url
        })
        this.$store.map.addLayer({ 
          "id": "hexagons",
          "type": "fill",
          "source": "hexagons",
          "source-layer": this.layerConfig.hexagons.source,
          "paint": {
            "fill-color": this.fillHexagons,
            "fill-opacity": 0.75
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'hexagons', status: true })
      }

      if (this.layerConfig.blokken_manual.active) {
        this.$store.map.addSource('blokken_manual', {
          type: 'vector',
          url: this.layerConfig.blokken_manual.url
        })
        this.$store.map.addLayer({ 
          "id": "blokken_manual",
          "type": "line",
          "source": "blokken_manual",
          "source-layer": this.layerConfig.blokken_manual.source,
          "paint": {
            "line-color": this.fillBlocks
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'blokken_manual', status: true })
      }

      if (this.layerConfig.blokken_auto.active) {
        this.$store.map.addSource('blokken_auto', {
          type: 'vector',
          url: this.layerConfig.blokken_auto.url
        })
        this.$store.map.addLayer({ 
          "id": "blokken_auto",
          "type": "line",
          "source": "blokken_auto",
          "source-layer": this.layerConfig.blokken_auto.source,
          "paint": {
            "line-color": this.fillAutoBlocks
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'blokken_auto', status: true })
      }

      if (this.layerConfig.hexagons.active) {
        this.$store.map.addLayer({ 
          "id": "hexagons_outline",
          "type": "line",
          "source": "hexagons",
          "source-layer": this.layerConfig.hexagons.source,
          "paint": {
            "line-color": this.buildingOutlineOpacity
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'hexagons_outline', status: true })
      }
    },
    // We want to add our layers below the labels
    // https://docs.mapbox.com/mapbox-gl-js/example/geojson-layer-in-stack/
    getFirstSymbolLayerId() {
      let layers = this.$store.map.getStyle().layers;
      // Find the index of the first symbol layer in the map style
      let firstSymbolId;
      for (let i = 0; i < layers.length; i++) {
        if (layers[i].type === 'symbol') {
          firstSymbolId = layers[i].id;
          break;
        }
      }

      return firstSymbolId
    },
    zoomToNeighborhood() {
      let coordinates = this.getNeighborhoodCoordinates({ neighborhood: this.getActiveNeighborhood })

      let bounds = coordinates.reduce((bounds, coord) => bounds.extend(coord), new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]))

      this.$store.map.fitBounds(bounds, {
        padding: 50
      })
    },
    /**
     * TODO: Implement Await and hint progress + add legend to output + update styling + add background to logo
     */
    downloadMap(e, control) {
      
      control.disable({ label: 'De pdf wordt samengesteld. Dit duurt even...' })

      setTimeout(() => {
        let title = `${this.getActiveMunicipalityInfo.label} - Wijkprofiel van ${this.getActiveNeighborhoodInfo.label}`
        let map = this.$store.map.getCanvas().toDataURL('image/png')

        generateExport({
          data: {
            title,
            map
          },
          callback: () => {
            control.enable()
          }
        })
      }, 1)
    }
  }
}
</script>

<style lang="scss">
  .mapboxgl-canvas-container, .mapboxgl-canvas {
    &:active, &:focus {
      outline: none;
    }
  }

  .mapboxgl-marker {
    position: absolute;
    cursor: pointer;
  }

  .mapboxgl-map {
    width: 100% !important;
    height: 100% !important;
  }

  .MapBox, .MapBox__wrapper {
    // display: flex;
    // align-items: center;
    // justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    user-select: none;
  }
  
  .MapBox__data {
    position: absolute;
    bottom: 2rem;
    right: 1rem;
  }

</style>
